// Workspace App — Legal page renderer.
// Fetches /api/workspace/legal/{terms|privacy|risk|account-terms|entity}
// and renders sections. Business-name-only, placeholder-safe.

import React from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";

const ROUTES = {
  terms: "terms",
  privacy: "privacy",
  risk: "risk",
  account: "account-terms",
};

export default function WorkspaceLegalPage() {
  const { kind } = useParams();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const path = ROUTES[kind] || "terms";
    api.get(`/workspace/legal/${path}`)
      .then(({ data }) => setData(data))
      .catch(() => setError(`Failed to load ${path}`));
  }, [kind]);

  if (error) return <div className="p-8 text-sm" data-testid="legal-error">{error}</div>;
  if (!data) return <div className="p-8 text-sm" data-testid="legal-loading">Loading…</div>;

  const e = data.entity || {};

  return (
    <div className="max-w-3xl mx-auto p-8" data-testid={`workspace-legal-${kind || "terms"}`}>
      <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
        {data.title}
      </h1>
      <div
        className="text-[10px] font-mono-data mb-6 pb-3 border-b"
        style={{ color: "var(--hoip-text-tertiary)", borderColor: "var(--hoip-border)" }}
        data-testid="legal-entity-line"
      >
        {e.product_name} · {e.legal_address} · {e.contact_email}
      </div>

      {e.pending_llc && (
        <div
          className="text-[11px] px-3 py-2 mb-6 border rounded-sm"
          style={{
            background: "rgba(245,158,11,0.08)",
            borderColor: "rgba(245,158,11,0.30)",
            color: "#F59E0B",
          }}
          data-testid="legal-pending-llc-banner"
        >
          <b>Interim legal entity.</b> LLC formation pending — this text
          will be finalized (product name, registered address, contact
          email) once the LLC is registered. Nothing here is boilerplate
          from an unrelated product.
        </div>
      )}

      {(data.sections || []).map((s, i) => (
        <section key={i} className="mb-6" data-testid={`legal-section-${i}`}>
          <h2 className="text-sm font-bold mb-2" style={{ color: "var(--hoip-text-primary)" }}>
            {s.heading}
          </h2>
          <p className="text-[13px] leading-6" style={{ color: "var(--hoip-text-secondary)" }}>
            {s.body}
          </p>
        </section>
      ))}
    </div>
  );
}
